import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * DropdownMenu component - A reusable dropdown menu with a trigger button
 * @param {React.ReactNode} trigger - The element that triggers the dropdown (defaults to three dots)
 * @param {Array} items - Array of menu items with { label, onClick, disabled, danger }
 * @param {string} className - Additional CSS classes
 */
const DropdownMenu = ({ trigger, items = [], className = "" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleItemClick = (item) => {
    if (item.disabled) return;
    if (item.onClick) {
      item.onClick();
    }
    setIsOpen(false);
  };

  const defaultTrigger = (
    <button
      ref={triggerRef}
      onClick={(e) => {
        e.stopPropagation();
        setIsOpen(!isOpen);
      }}
      className="p-1 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300"
      aria-label="More options"
    >
      <svg
        className="w-5 h-5 text-gray-600"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
      </svg>
    </button>
  );

  // Calculate dropdown position when open
  const [position, setPosition] = useState({ top: 0, right: 0 });
  const containerRef = useRef(null);

  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 4,
        right: window.innerWidth - rect.right - window.scrollX,
      });
    }
  }, [isOpen]);

  const dropdownContent = isOpen && (
    <div
      ref={menuRef}
      className="fixed z-[9999] w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none"
      onClick={(e) => e.stopPropagation()}
      style={{
        top: `${position.top}px`,
        right: `${position.right}px`,
      }}
    >
      <div className="py-1" role="menu">
        {items.map((item, index) => (
          <button
            key={index}
            onClick={() => handleItemClick(item)}
            disabled={item.disabled}
            className={`${
              item.disabled
                ? "opacity-50 cursor-not-allowed"
                : item.danger
                ? "text-red-700 hover:bg-red-50"
                : "text-gray-700 hover:bg-gray-100"
            } block w-full text-left px-4 py-2 text-sm`}
            role="menuitem"
          >
            {item.label}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {trigger || defaultTrigger}
      {typeof document !== "undefined" &&
        createPortal(dropdownContent, document.body)}
    </div>
  );
};

export default DropdownMenu;
